import { useQuery } from '@apollo/client';
import { useMemo, useCallback } from 'react';
import { GET_PSA } from '../../services/apollo/queries/psaQueries';
import { GET_LOST_ANIMAL } from '../../services/apollo/queries/lostAnimalQueries';
import { GET_FOUND_ANIMAL } from '../../services/apollo/queries/foundAnimalQueries';
import { GET_PET_SERVICE } from '../../services/apollo/queries/petServiceQueries';
import { FEED_TYPES } from './useCombinedFeed';
import { normalizePostType } from '../../utils/postTypeNormalize';

const DETAIL_FETCH_POLICY = 'cache-and-network';

/**
 * Hook to fetch a single notification by ID

 * @param {string}  id - The notification ID
 * @param {string=} knownType
 * @returns {Object} { data, loading, error, type, refetch }
 */
const useFetchNotificationById = (id, knownType) => {
  const normalized = normalizePostType(knownType);
  const skipPsa = !id || (normalized && normalized !== FEED_TYPES.PSA);
  const skipLost = !id || (normalized && normalized !== FEED_TYPES.LOST);
  const skipFound = !id || (normalized && normalized !== FEED_TYPES.FOUND);
  const skipPetService =
    !id || (normalized && normalized !== FEED_TYPES.PET_SERVICE);

  const {
    data: psaData,
    loading: psaLoading,
    error: psaError,
    refetch: refetchPsa,
  } = useQuery(GET_PSA, {
    variables: { _id: id },
    skip: skipPsa,
    errorPolicy: 'all',
    fetchPolicy: DETAIL_FETCH_POLICY,
  });

  const {
    data: lostData,
    loading: lostLoading,
    error: lostError,
    refetch: refetchLost,
  } = useQuery(GET_LOST_ANIMAL, {
    variables: { _id: id },
    skip: skipLost,
    errorPolicy: 'all',
    fetchPolicy: DETAIL_FETCH_POLICY,
  });

  const {
    data: foundData,
    loading: foundLoading,
    error: foundError,
    refetch: refetchFound,
  } = useQuery(GET_FOUND_ANIMAL, {
    variables: { _id: id },
    skip: skipFound,
    errorPolicy: 'all',
    fetchPolicy: DETAIL_FETCH_POLICY,
  });

  const {
    data: petServiceData,
    loading: petServiceLoading,
    error: petServiceError,
    refetch: refetchPetService,
  } = useQuery(GET_PET_SERVICE, {
    variables: { _id: id },
    skip: skipPetService,
    errorPolicy: 'all',
    fetchPolicy: DETAIL_FETCH_POLICY,
  });

  // Determine which type was found
  const result = useMemo(() => {
    // Check PSA
    if (psaData?.getPSA) {
      return {
        data: psaData.getPSA,
        type: FEED_TYPES.PSA,
        loading: false,
        error: null,
      };
    }

    // Check Lost Animal
    if (lostData?.getLostAnimal) {
      return {
        data: lostData.getLostAnimal,
        type: FEED_TYPES.LOST,
        loading: false,
        error: null,
      };
    }

    // Check Found Animal
    if (foundData?.getFoundAnimal) {
      return {
        data: foundData.getFoundAnimal,
        type: FEED_TYPES.FOUND,
        loading: false,
        error: null,
      };
    }

    // Check Pet Service
    if (petServiceData?.getPetService) {
      return {
        data: petServiceData.getPetService,
        type: FEED_TYPES.PET_SERVICE,
        loading: false,
        error: null,
      };
    }

    // Still loading
    if (psaLoading || lostLoading || foundLoading || petServiceLoading) {
      return {
        data: null,
        type: null,
        loading: true,
        error: null,
      };
    }

    // All queries failed - notification not found
    const allFailed = psaError && lostError && foundError && petServiceError;
    if (allFailed) {
      return {
        data: null,
        type: null,
        loading: false,
        error: new Error('Notification not found'),
      };
    }

    // Default loading state
    return {
      data: null,
      type: null,
      loading: psaLoading || lostLoading || foundLoading || petServiceLoading,
      error: null,
    };
  }, [
    psaData, lostData, foundData, petServiceData,
    psaLoading, lostLoading, foundLoading, petServiceLoading,
    psaError, lostError, foundError, petServiceError
  ]);

  // Refetch function that refetches the appropriate query based on type
  const refetch = useCallback(async () => {
    if (result.type === FEED_TYPES.PSA) {
      return refetchPsa();
    }
    if (result.type === FEED_TYPES.LOST) {
      return refetchLost();
    }
    if (result.type === FEED_TYPES.FOUND) {
      return refetchFound();
    }
    if (result.type === FEED_TYPES.PET_SERVICE) {
      return refetchPetService();
    }
    // If type not determined yet, refetch all
    return Promise.all([refetchPsa(), refetchLost(), refetchFound(), refetchPetService()]);
  }, [result.type, refetchPsa, refetchLost, refetchFound, refetchPetService]);

  return { ...result, refetch };
};

export default useFetchNotificationById;

